import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ShoppingCart, Package, FileText, Truck, CheckCircle2, AlertTriangle,
  Plus, Search, X, Loader2, Building2, ClipboardList, Receipt,
  ArrowRight, BadgeCheck, Clock, DollarSign, TrendingUp, Banknote,
  ChevronDown, ChevronRight, Eye, BarChart2, MapPin, Filter,
  Check, RotateCcw, Calendar, AlertCircle, Boxes, MessageSquare,
  Download, Printer, History, FileCheck, Pencil, Paperclip, UploadCloud,
  List, BookOpen, Tag, ExternalLink, Brain, Sparkles, FileUp, CheckCheck, Zap, Route
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatPYGCompact, formatDate, todayAsuncion, parseDateOnly } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { Modal, StatCard, StatusBadge, StatusTimeline, PedidoDetailModal, HistorialPedidoCard } from "./SharedComponents";
import { FLOW_STEPS, STATUS_COLORS, STATUS_LABELS, UNITS, WORK_FRONTS, URGENCY_LABELS } from "./constants";
import { formatDateTime, buildHistory, generatePedidoPDF, generatePPPDF, generateAPPDF, guardarPDF, agruparPorMaterial } from "./helpers";
import { FiltroClienteObraPresupuesto, matchClienteObraPresupuesto } from "./FiltroClienteObraPresupuesto";
import { BusquedaEstadoUrgenciaFilters } from "./BusquedaFilters";
import { roleAllowed } from "../../utils/roles";
import { ItinerarioTab } from "./ItinerarioTab";
import SelectBuscable from "../../components/SelectBuscable";   // Fix EI (2026-09-27)

// Fix FF (2026-10-08): "Encuentro que se repite el mismo pedido a la hora de realizar el retiro"
// (CEN). La OC trae una línea por ítem del presupuesto (Fix EG); en Retiro/Entrega se ven y se
// cargan por material (misma descripción, unidad y P.U.) con las cantidades sumadas. Lo que se
// carga para un material se reparte entre sus líneas en la proporción de lo pedido (la última
// absorbe el redondeo): el retiro, la entrega y los faltantes siguen por ítem del presupuesto.
const _r4 = v => Math.round((Number(v) || 0) * 10000) / 10000;
const _unir = vals => [...new Set(vals.map(v => String(v || "").trim()).filter(Boolean))].join(" · ");
const _retirada = l => (l.cantidad_retirada != null ? Number(l.cantidad_retirada) || 0 : (l.retirado ? Number(l.quantity) || 0 : 0));
const _entregada = l => (l.cantidad_entregada != null ? Number(l.cantidad_entregada) || 0 : (l.entregado ? _retirada(l) : 0));
function agruparRetiro(items) {
  const lista = items || [];
  return agruparPorMaterial(lista, true).map(g => {
    const lineas = g.__idx.map(i => lista[i]);
    const suma = f => _r4(lineas.reduce((s, l) => s + f(l), 0));
    const conFecha = lineas.filter(l => l.fecha_limite_uso);
    return {
      ...g,
      cantidad_retirada: suma(_retirada),
      cantidad_entregada: suma(_entregada),
      _cantidad_retirada_base: suma(l => Number(l._cantidad_retirada_base ?? l.quantity) || 0),
      retirado: lineas.every(l => l.retirado),
      entregado: lineas.every(l => l.entregado),
      indicaciones: _unir(lineas.map(l => l.indicaciones)),
      retiro_notes: _unir(lineas.map(l => l.retiro_notes)),
      delivery_notes: _unir(lineas.map(l => l.delivery_notes)),
      fecha_limite_uso: conFecha.length ? conFecha[0].fecha_limite_uso : null,
      sobrante_retirado: conFecha.every(l => l.sobrante_retirado),
    };
  });
}
// Reparte `total` entre las líneas `idxs` según el peso de cada una.
function repartirEnLineas(lineas, idxs, total, peso) {
  const pesos = idxs.map(i => Number(peso(lineas[i])) || 0);
  const suma = pesos.reduce((s, x) => s + x, 0);
  let asignado = 0;
  return idxs.map((i, k) => {
    if (k === idxs.length - 1) return _r4(total - asignado);
    const v = suma > 0 ? _r4((total * pesos[k]) / suma) : 0;
    asignado = _r4(asignado + v);
    return v;
  });
}
// Aplica `cambio(linea, valorRepartido)` a las líneas del material.
function actualizarGrupo(lineas, g, total, peso, cambio) {
  const ni = [...lineas];
  const partes = total == null ? null : repartirEnLineas(lineas, g.__idx, total, peso);
  g.__idx.forEach((i, k) => { ni[i] = cambio(ni[i], partes ? partes[k] : null); });
  return ni;
}
// El aviso de faltantes, por material.
const lineasFaltante = (items) => {
  const m = new Map();
  (items || []).forEach(fi => {
    const k = `${fi.item_name}|${fi.unit}`;
    m.set(k, { ...fi, faltante_qty: _r4((m.get(k)?.faltante_qty || 0) + (Number(fi.faltante_qty) || 0)) });
  });
  return [...m.values()].map(fi => `• ${fi.item_name}: faltan ${fi.faltante_qty} ${fi.unit}`).join("\n");
};

// BLOQUE 57 PARTE A — tope de remisiones adjuntas por retiro (antes: 1 sola,
// se pisaba en cada subida). Debe coincidir con MAX_WD_REMISIONES del backend.
const MAX_WD_REMISIONES = 10;

// Lista unificada de remisiones de un retiro: usa `documents[]` (Bloque 57) si
// existe; si es un retiro legado con 1 sola remisión en doc_data/doc_name, la
// envuelve en una lista de 1 elemento — nunca se pierde una remisión ya adjunta.
function getWdDocs(wd) {
  if (!wd) return [];
  const docs = Array.isArray(wd.documents) ? wd.documents : [];
  if (docs.length === 0 && wd.doc_data) {
    return [{ doc_data: wd.doc_data, doc_name: wd.doc_name || "remito", doc_type: wd.doc_type || "" }];
  }
  return docs;
}

export function RetiroTab({
  withdrawals, ocFromPF, loading, onRefresh, userRole,
  listasClientes, listasObras, allBudgets, openNumber,
  filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto,
  // BLOQUE 44 — modo restringido del Operario Electricista: solo ve/confirma
  // la sección "Entrega en Obra" (nunca "Retiro de Materiales" ni montos).
  operarioMode = false,
}) {
  const [subTab, setSubTab] = useState(operarioMode ? "entrega" : "retiro");
  const fileInputRef = useRef(null);
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [viewWD, setViewWD] = useState(null);
  const [closeRetiroWD, setCloseRetiroWD] = useState(null);
  const [closeDeliveryWD, setCloseDeliveryWD] = useState(null);

  // BLOQUE 57 PARTE A — mantiene el modal de detalle sincronizado con la lista
  // fresca de retiros luego de subir/eliminar una remisión (onRefresh actualiza
  // `withdrawals` en el padre, pero `viewWD` era una foto congelada al abrir).
  useEffect(() => {
    if (viewWD) {
      const updated = withdrawals.find(w => w._id === viewWD._id);
      if (updated) setViewWD(updated);
    }
  }, [withdrawals]); // eslint-disable-line react-hooks/exhaustive-deps

  // BLOQUE 34 — deep-link desde el Dashboard: abre directamente el retiro
  // clickeado (?open=<numero RP>), ubicándose en la sub-pestaña correcta
  // (pendiente de retiro vs. pendiente de entrega) según su status real.
  const openedRef = useRef(false);
  useEffect(() => {
    if (!openNumber || openedRef.current || !withdrawals.length) return;
    const target = withdrawals.find(w => w.number === openNumber);
    if (target) {
      setSubTab(target.status === "retiro_cerrado" ? "entrega" : "retiro");
      setViewWD(target);
      openedRef.current = true;
    }
  }, [openNumber, withdrawals]);

  // New withdrawal state
  const [selectedOC, setSelectedOC] = useState(null);
  const [newNotes, setNewNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // BLOQUE 40 PARTE C — indicaciones por ítem (edición exclusiva del Jefe de Compras)
  const [newIndic, setNewIndic] = useState({});   // {idx: texto} en el modal de creación
  const [indicWD, setIndicWD] = useState(null);   // retiro en edición de indicaciones
  const [indicItems, setIndicItems] = useState([]);
  const [savingIndic, setSavingIndic] = useState(false);


  // Close retiro state
  const [retiroItems, setRetiroItems] = useState([]);
  const [retiroNotes, setRetiroNotes] = useState("");

  // Close delivery state
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState("");
  // BLOQUE 50 PARTE A — Buscar + Estado + Urgencia
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterUrgency, setFilterUrgency] = useState("");

  const canLogistics = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras", "Jefe de Logística", "Coordinador de Obra"]);
  // BLOQUE 40 PARTE A — el Residente de Obra (canónico + alias legacy "Ingeniero
  // Residente") también marca el retiro y adjunta el remito. CREAR retiros sigue
  // siendo exclusivo de Compras/Logística/gestión (canLogistics).
  const canCloseRetiro = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras", "Jefe de Logística", "Coordinador de Obra", "Residente de Obra", "Ingeniero Residente"]);
  // BLOQUE 44 — el Operario Electricista se agrega a quienes pueden confirmar
  // la Entrega en Obra (NO al retiro, que sigue siendo de Compras/Logística).
  const canResidente = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Ingeniero Residente", "Residente de Obra", "Coordinador de Obra", "Jefe de Compras", "Jefe de Logística", "Operario Electricista"]);
  // BLOQUE 40 PARTE C — las indicaciones por ítem las edita SOLO el Jefe de Compras
  // (+ Superadmin/Admin); el resto de los perfiles habilitados las ve en modo lectura.
  const canEditIndicaciones = roleAllowed(userRole, ["Superadministrador", "Administrador", "Jefe de Compras", "Compras"]);
  // BLOQUE 55 — el itinerario lo arma/edita SOLO el Jefe de Compras (+ Superadmin/Admin).
  const canEditItinerario = roleAllowed(userRole, ["Superadministrador", "Administrador", "Jefe de Compras", "Compras"]);

  // BLOQUE 55 — modal "Agregar al Itinerario" (desde la card de Retiro o de Entrega).
  const [addItinWD, setAddItinWD] = useState(null);   // { wd, tipo }
  // Fix AH (2026-09-04): parada del itinerario desde la que se abrió el detalle
  // (null si se abrió con VER desde la lista). Se limpia al cerrar el modal.
  const [viewStop, setViewStop] = useState(null);
  const [soloParada, setSoloParada] = useState(true);
  useEffect(() => {
    if (!viewWD) { setViewStop(null); setSoloParada(true); }
  }, [viewWD]);
  const stopActivo = viewStop && viewWD && viewStop.withdrawal_id === viewWD._id ? viewStop : null;
  const [itinFecha, setItinFecha] = useState(todayAsuncion());
  const [itinSucursal, setItinSucursal] = useState("");
  const [itinNotes, setItinNotes] = useState("");
  const [itinItemsSel, setItinItemsSel] = useState({});  // { idx: true }
  const [savingItin, setSavingItin] = useState(false);

  const openAddItinerario = (wd, tipo) => {
    setAddItinWD({ wd, tipo });
    setItinFecha(todayAsuncion());
    setItinSucursal(tipo === "retiro" ? (wd.sucursal || "") : "");
    setItinNotes("");
    setItinItemsSel({});
  };

  const handleAddToItinerario = async () => {
    if (!addItinWD) return;
    setSavingItin(true);
    try {
      const selectedIdx = Object.keys(itinItemsSel).filter(k => itinItemsSel[k]).map(Number);
      await axios.post(`${API}/itinerary`, {
        withdrawal_id: addItinWD.wd._id,
        tipo: addItinWD.tipo,
        fecha: itinFecha,
        sucursal: itinSucursal,
        notes: itinNotes,
        items_idx: selectedIdx.length > 0 ? selectedIdx : null,
      }, { withCredentials: true });
      toast.success("Parada agregada al itinerario");
      setAddItinWD(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al agregar al itinerario"); }
    finally { setSavingItin(false); }
  };

  const ocUrgencyById = {};
  ocFromPF.forEach(oc => { ocUrgencyById[oc._id] = oc.urgency; });
  const baseFilteredWDs = withdrawals.filter(wd => matchClienteObraPresupuesto(wd, { filterCliente, filterObra, filterPresupuesto }, { listasClientes, listasObras }));
  const wdStatusCounts = baseFilteredWDs.reduce((acc, w) => { acc[w.status] = (acc[w.status] || 0) + 1; return acc; }, {});
  const filteredWDsUnsorted = baseFilteredWDs.filter(wd => {
    if (filterStatus !== "todos" && wd.status !== filterStatus) return false;
    if (filterUrgency && (ocUrgencyById[wd.oc_id] || "normal") !== filterUrgency) return false;
    if (search) {
      const s = search.toLowerCase();
      const hit = (wd.number || "").toLowerCase().includes(s) || (wd.oc_number || "").toLowerCase().includes(s) ||
        (wd.project_name || "").toLowerCase().includes(s) || (wd.provider_name || "").toLowerCase().includes(s) ||
        (wd.delivery_number || "").toLowerCase().includes(s);
      if (!hit) return false;
    }
    return true;
  });
  // BLOQUE 57 PARTE B — un retiro/entrega "de faltante" solo debe alarmar mientras
  // esa ETAPA puntual sigue pendiente: si es de tipo "retiro", solo mientras
  // status=="pendiente" (una vez cerrado el retiro ya no falta nada de esa etapa);
  // si es de tipo "entrega", solo mientras status=="retiro_cerrado" (pendiente de
  // confirmar en obra). Antes se usaba `status !== "entregado"`, que dejaba la
  // alarma prendida incluso con el retiro ya cerrado y 100% completo.
  const esFaltantePendiente = (w) => {
    if (!w.es_faltante) return false;
    if (w.faltante_tipo === "retiro") return w.status === "pendiente";
    if (w.faltante_tipo === "entrega") return w.status === "retiro_cerrado";
    return w.status !== "entregado";
  };
  // BLOQUE 51 PARTE A — orden en 3 niveles: 0 = faltante activo (alarma, siempre
  // arriba de todo), 1 = pendiente normal, 2 = completado; dentro de cada nivel,
  // el más nuevo primero (convive con el orden de 2 niveles del Bloque 50).
  const faltanteRank = (w) => {
    if (esFaltantePendiente(w)) return 0;
    return w.status !== "entregado" ? 1 : 2;
  };
  const filteredWDs = [...filteredWDsUnsorted].sort((a, b) => {
    const r = faltanteRank(a) - faltanteRank(b);
    if (r !== 0) return r;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
  const pendingWDs = filteredWDs.filter(w => w.status === "pendiente");
  const closedRetiroWDs = filteredWDs.filter(w => w.status === "retiro_cerrado");
  const deliveredWDs = filteredWDs.filter(w => w.status === "entregado");
  // Las líneas de faltante generadas por una ENTREGA incompleta ya están
  // "retiradas" (el material está en poder de CEN) — deben verse SOLO en la
  // sub-pestaña "Entrega en Obra", nunca en "Retiro de Materiales", para que
  // Logística no crea que hay que volver a buscarlas al proveedor.
  const retiroSubtabWDs = filteredWDs.filter(w => !(w.es_faltante && w.faltante_tipo === "entrega"));
  const faltantesRetiroCount = filteredWDs.filter(w => w.faltante_tipo === "retiro" && esFaltantePendiente(w)).length;
  const faltantesEntregaCount = filteredWDs.filter(w => w.faltante_tipo === "entrega" && esFaltantePendiente(w)).length;
  const faltantesCount = faltantesRetiroCount + faltantesEntregaCount;

  // OCs eligible for new withdrawal (aprobada / facturada / facturada parcial y sin retiro)
  const eligibleOCs = ocFromPF.filter(oc =>
    ["aprobada", "facturada", "facturada_parcial"].includes(oc.status) &&
    !withdrawals.find(w => w.oc_id === oc._id && w.status !== "cancelado")
  );

  const handleSelectOC = (ocId) => {
    const oc = ocFromPF.find(o => o._id === ocId);
    setSelectedOC(oc || null);
  };

  const handleUploadDoc = (wd) => {
    const docs = getWdDocs(wd);
    if (docs.length >= MAX_WD_REMISIONES) {
      toast.error(`Ya se alcanzó el máximo de ${MAX_WD_REMISIONES} remisiones para este retiro. Elimine alguna para poder subir otra.`);
      return;
    }
    setUploadingDocId(wd._id);
    fileInputRef.current.click();
  };

  const handleDeleteDoc = async (wdId, docIndex) => {
    if (!window.confirm("¿Eliminar esta remisión adjunta? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/purchases/material-withdrawals/${wdId}/documents/${docIndex}`, { withCredentials: true });
      toast.success("Remisión eliminada");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar remisión");
    }
  };

  // Fix O (2026-08-31): apaga el aviso "Retirar sobrante de material" (punto 14
  // de la carta) — el aviso vivía por ítem (fecha_limite_uso/sobrante_retirado
  // se fijan por ítem al confirmar la entrega, no por documento), así que el
  // endpoint PUT /material-withdrawals/{id}/marcar-sobrante-retirado espera el
  // índice del ítem, no un body vacío.
  const handleMarcarSobrante = async (wdId, index) => {
    try {
      for (const i of [].concat(index)) {   // Fix FF: todas las líneas del material
        await axios.put(`${API}/purchases/material-withdrawals/${wdId}/marcar-sobrante-retirado`, { index: i }, { withCredentials: true });
      }
      toast.success("Sobrante marcado como retirado");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo marcar el sobrante");
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingDocId) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("El archivo supera 3MB. Comprima la imagen antes de subir.");
      e.target.value = "";
      setUploadingDocId(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await axios.put(`${API}/purchases/material-withdrawals/${uploadingDocId}/upload-doc`, {
          file_data: ev.target.result,
          file_name: file.name,
          file_type: file.type,
        }, { withCredentials: true });
        toast.success("Documento adjuntado correctamente");
        onRefresh();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al subir documento");
      } finally {
        setUploadingDocId(null);
        e.target.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCreateWithdrawal = async () => {
    if (!selectedOC) return;
    setSaving(true);
    try {
      const payload = { oc_id: selectedOC._id, notes: newNotes };
      if (canEditIndicaciones) {
        payload.items_indicaciones = (selectedOC.items || []).map((_, i) => newIndic[i] || "");
      }
      await axios.post(`${API}/purchases/material-withdrawals`, payload, { withCredentials: true });
      toast.success("Retiro creado correctamente");
      setShowNewModal(false);
      setSelectedOC(null);
      setNewNotes("");
      setNewIndic({});
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear retiro"); }
    finally { setSaving(false); }
  };

  // BLOQUE 40 PARTE C — persiste las indicaciones SIN cerrar el retiro.
  const handleSaveIndicaciones = async () => {
    if (!indicWD) return;
    setSavingIndic(true);
    try {
      const itemsPayload = indicItems.map((item, idx) => ({ index: idx, indicaciones: item.indicaciones || "" }));
      await axios.put(`${API}/purchases/material-withdrawals/${indicWD._id}/item-indications`, { items: itemsPayload }, { withCredentials: true });
      toast.success("Indicaciones guardadas");
      setIndicWD(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar indicaciones"); }
    finally { setSavingIndic(false); }
  };

  const handleCloseRetiro = async (confirmIncomplete = false) => {
    if (!closeRetiroWD) return;
    setSaving(true);
    try {
      const itemsPayload = retiroItems.map((item, idx) => ({
        index: idx,
        cantidad_retirada: Number(item.cantidad_retirada ?? 0),
        retiro_notes: item.retiro_notes || "",
      }));
      const { data: resp } = await axios.put(`${API}/purchases/material-withdrawals/${closeRetiroWD._id}/close-retiro`, { items: itemsPayload, notes: retiroNotes, confirm_incomplete: confirmIncomplete }, { withCredentials: true });
      toast.success(resp.faltante_generado ? `Retiro cerrado. Se generó ${resp.faltante_generado.number} con lo faltante.` : "Retiro cerrado correctamente");
      setCloseRetiroWD(null);
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // BLOQUE 51 — cierre incompleto: pedir confirmación explícita antes de generar el faltante.
      if (err.response?.status === 409 && detail?.code === "incomplete_confirm_required") {
        const lines = lineasFaltante(detail.faltante_items);   // Fix FF
        if (window.confirm(`${detail.message}\n\n${lines}`)) {
          await handleCloseRetiro(true);
          return;
        }
      } else {
        toast.error(detail || "Error al cerrar retiro");
      }
    } finally { setSaving(false); }
  };

  const handleCloseDelivery = async (confirmIncomplete = false) => {
    if (!closeDeliveryWD) return;
    setSaving(true);
    try {
      const itemsPayload = deliveryItems.map((item, idx) => ({
        index: idx,
        cantidad_entregada: Number(item.cantidad_entregada ?? 0),
        delivery_notes: item.delivery_notes || "",
      }));
      const { data: resp } = await axios.put(`${API}/purchases/material-withdrawals/${closeDeliveryWD._id}/close-delivery`, { items: itemsPayload, notes: deliveryNotes, confirm_incomplete: confirmIncomplete }, { withCredentials: true });
      toast.success(resp.faltante_generado ? `Entrega confirmada. Se generó ${resp.faltante_generado.number} con lo faltante.` : "Entrega en obra confirmada");
      setCloseDeliveryWD(null);
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "incomplete_confirm_required") {
        const lines = lineasFaltante(detail.faltante_items);   // Fix FF
        if (window.confirm(`${detail.message}\n\n${lines}`)) {
          await handleCloseDelivery(true);
          return;
        }
      } else {
        toast.error(detail || "Error al confirmar entrega");
      }
    } finally { setSaving(false); }
  };

  const generateREPDF = (wd) => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFillColor(46, 109, 164);
    doc.rect(0, 0, pageW, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14); doc.setFont(undefined, "bold");
    doc.text("CEN INGENIERÍA E.A.S.", 14, 11);
    doc.setFontSize(9); doc.setFont(undefined, "normal");
    doc.text("REMISIÓN DE RETIRO DE MATERIALES", 14, 18);
    doc.setFontSize(12); doc.setFont(undefined, "bold");
    doc.text(wd.number, pageW - 14, 14, { align: "right" });
    doc.setFontSize(8); doc.setFont(undefined, "normal");
    doc.text(formatDate(wd.created_at), pageW - 14, 21, { align: "right" });
    doc.setTextColor(0, 0, 0);
    let y = 36;
    const fieldRow = (x, yy, label, value) => {
      doc.setFontSize(7); doc.setFont(undefined, "bold"); doc.setTextColor(120, 120, 120);
      doc.text(label.toUpperCase(), x, yy);
      doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30); doc.setFontSize(9);
      doc.text(String(value || "—"), x, yy + 5);
    };
    const c2 = pageW / 2 + 2;
    fieldRow(14, y, "OC Referencia", wd.oc_number); fieldRow(c2, y, "Proveedor", wd.provider_name); y += 14;
    fieldRow(14, y, "Proyecto", wd.project_name); fieldRow(c2, y, "Sucursal", wd.sucursal || "—"); y += 14;
    fieldRow(14, y, "Creado por", wd.created_by_name);
    if (wd.retiro_closed_by_name) fieldRow(c2, y, "Cerrado por", wd.retiro_closed_by_name);
    y += 16;
    autoTable(doc, {
      startY: y,
      // BLOQUE 40 PARTE C — la columna "Indicaciones" (Jefe de Compras) viaja en el PDF
      head: [["#", "Descripción", "UM", "Pedido", "P.U.", "Subtotal", "Retirado", "Indicaciones"]],
      body: agruparRetiro(wd.items).map((item, i) => [i + 1, item.item_name, item.unit, item.quantity, formatPYG(item.unit_price), formatPYG(item.subtotal), item.cantidad_retirada != null ? `${item.cantidad_retirada}/${item.quantity}` : (item.retirado ? "SI" : "NO"), item.indicaciones || "—"]),   // Fix FF
      styles: { fontSize: 8, cellPadding: 2.5, overflow: "linebreak" },
      headStyles: { fillColor: [46, 109, 164], textColor: 255, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 249, 255] },
      // Anchos fijos: montos y columnas cortas con overflow "visible" (nunca se
      // parten a mitad de número — criterio del Bloque 26); descripción e
      // indicaciones envuelven por palabra. Suma = 182mm (A4 vertical, márgenes 14).
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 42 },
        2: { cellWidth: 12, halign: "center", overflow: "visible" },
        3: { cellWidth: 14, halign: "right", overflow: "visible" },
        4: { cellWidth: 26, halign: "right", overflow: "visible" },
        5: { cellWidth: 28, halign: "right", overflow: "visible" },
        6: { cellWidth: 19, halign: "center", overflow: "visible" },
        7: { cellWidth: 33 },
      }
    });
    const totY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(8); doc.setFont(undefined, "bold");
    doc.text(`Estado: ${STATUS_LABELS[wd.status] || wd.status}`, 14, totY);
    let extraY = totY;
    if (wd.es_faltante) {
      extraY += 6;
      const pend = esFaltantePendiente(wd);
      if (pend) doc.setTextColor(200, 0, 0); else doc.setTextColor(120, 120, 120);
      doc.text(`${pend ? "RETIRO INCOMPLETO — Faltante generado por" : "Generado por faltante de"} ${wd.origen_retiro_number}`, 14, extraY);
      doc.setTextColor(0, 0, 0);
    }
    if (wd.faltante_hijo_number) { extraY += 6; doc.setTextColor(200, 0, 0); doc.text(`Se generó un retiro con el faltante: ${wd.faltante_hijo_number}`, 14, extraY); doc.setTextColor(0, 0, 0); }
    if (wd.notes) { extraY += 6; doc.setFont(undefined, "normal"); doc.setFontSize(8); doc.text(`Obs: ${wd.notes}`, 14, extraY); }
    const footY = doc.internal.pageSize.getHeight() - 10;
    doc.setFontSize(7); doc.setTextColor(150, 150, 150); doc.setFont(undefined, "normal");
    doc.text(`Generado: ${new Date().toLocaleString("es-PY")} · CEN Ingeniería E.A.S. · Sistema ERP`, pageW / 2, footY, { align: "center" });
    guardarPDF(doc, `${wd.number}.pdf`);
  };

  const generateENPDF = (wd) => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFillColor(34, 197, 94);
    doc.rect(0, 0, pageW, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14); doc.setFont(undefined, "bold");
    doc.text("CEN INGENIERÍA E.A.S.", 14, 11);
    doc.setFontSize(9); doc.setFont(undefined, "normal");
    doc.text("CONFIRMACIÓN DE ENTREGA EN OBRA", 14, 18);
    doc.setFontSize(12); doc.setFont(undefined, "bold");
    doc.text(wd.delivery_number || "", pageW - 14, 14, { align: "right" });
    doc.setFontSize(8); doc.setFont(undefined, "normal");
    doc.text(formatDate(wd.delivery_closed_at), pageW - 14, 21, { align: "right" });
    doc.setTextColor(0, 0, 0);
    let y = 36;
    const fieldRow = (x, yy, label, value) => {
      doc.setFontSize(7); doc.setFont(undefined, "bold"); doc.setTextColor(120, 120, 120);
      doc.text(label.toUpperCase(), x, yy);
      doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30); doc.setFontSize(9);
      doc.text(String(value || "—"), x, yy + 5);
    };
    const c2 = pageW / 2 + 2;
    fieldRow(14, y, "N° Remisión (RE)", wd.number); fieldRow(c2, y, "N° Entrega (EN)", wd.delivery_number || ""); y += 14;
    fieldRow(14, y, "OC Referencia", wd.oc_number); fieldRow(c2, y, "Proveedor", wd.provider_name); y += 14;
    fieldRow(14, y, "Proyecto", wd.project_name); fieldRow(c2, y, "Recibido por", wd.delivery_closed_by_name || "—"); y += 16;
    autoTable(doc, {
      startY: y,
      head: [["#", "Descripción", "UM", "Retirado", "Entregado", "Obs."]],
      body: agruparRetiro(wd.items).map((item, i) => {   // Fix FF
        const retirada = item.cantidad_retirada != null ? item.cantidad_retirada : (item.retirado ? item.quantity : 0);
        const entregada = item.cantidad_entregada != null ? item.cantidad_entregada : (item.entregado ? retirada : 0);
        return [i + 1, item.item_name, item.unit, retirada, `${entregada}/${retirada}`, item.delivery_notes || ""];
      }),
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: [240, 253, 244] },
      columnStyles: { 0: { cellWidth: 8 }, 2: { cellWidth: 14, halign: "center" }, 3: { cellWidth: 18, halign: "right" }, 4: { cellWidth: 22, halign: "center" } }
    });
    const footY = doc.internal.pageSize.getHeight() - 10;
    let noteY = doc.lastAutoTable.finalY + 8;
    if (wd.es_faltante) {
      const pend = esFaltantePendiente(wd);
      doc.setFontSize(8);
      if (pend) doc.setTextColor(200, 120, 0); else doc.setTextColor(120, 120, 120);
      doc.text(`${pend ? "ENTREGA INCOMPLETA — Faltante generado por" : "Generado por faltante de"} ${wd.origen_retiro_number}`, 14, noteY);
      doc.setTextColor(0, 0, 0); noteY += 6;
    }
    if (wd.faltante_hijo_number) { doc.setFontSize(8); doc.setTextColor(200, 120, 0); doc.text(`Se generó una nueva entrega con el faltante: ${wd.faltante_hijo_number}`, 14, noteY); doc.setTextColor(0, 0, 0); }
    doc.setFontSize(7); doc.setTextColor(150, 150, 150); doc.setFont(undefined, "normal");
    doc.text(`Generado: ${new Date().toLocaleString("es-PY")} · CEN Ingeniería E.A.S. · Sistema ERP`, pageW / 2, footY, { align: "center" });
    guardarPDF(doc, `${wd.delivery_number || "EN-entrega"}.pdf`);
  };

  return (
    <div className="space-y-4" data-testid={operarioMode ? "entrega-obra-tab" : "retiro-tab"}>
      {/* Hidden file input for document upload */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*,application/pdf"
        onChange={handleFileSelected}
      />
      {/* Filtro Cliente -> Obra -> Presupuesto (BLOQUE 27) + Buscar/Estado/Urgencia (BLOQUE 50) */}
      <div className="flex items-center gap-2 flex-wrap">
        <FiltroClienteObraPresupuesto
          listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets}
          filterCliente={filterCliente} setFilterCliente={setFilterCliente}
          filterObra={filterObra} setFilterObra={setFilterObra}
          filterPresupuesto={filterPresupuesto} setFilterPresupuesto={setFilterPresupuesto}
          testIdPrefix="retiro"
        />
        <BusquedaEstadoUrgenciaFilters
          search={search} setSearch={setSearch}
          searchPlaceholder="Buscar por N° RE, OC, obra o proveedor..."
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          statusOptions={[
            { value: "todos", label: `Todos (${baseFilteredWDs.length})` },
            { value: "pendiente", label: `Pendiente (${wdStatusCounts.pendiente || 0})` },
            { value: "retiro_cerrado", label: `Retiro Cerrado (${wdStatusCounts.retiro_cerrado || 0})` },
            { value: "entregado", label: `Entregado (${wdStatusCounts.entregado || 0})` },
          ]}
          filterUrgency={filterUrgency} setFilterUrgency={setFilterUrgency}
          testIdPrefix="retiro"
        />
        {(filterCliente || filterObra || filterPresupuesto || search || filterUrgency || filterStatus !== "todos") && (
          <button onClick={() => { setFilterCliente(""); setFilterObra(""); setFilterPresupuesto(""); setSearch(""); setFilterUrgency(""); setFilterStatus("todos"); }}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl px-2 py-2 hover:bg-gray-50 transition-all" title="Limpiar filtros"
            data-testid="retiro-clear-filter">
            <X size={12} /> Limpiar
          </button>
        )}
      </div>

      {/* BLOQUE 51 — alarma de faltantes: se apila siempre primero en los listados
          y además se resalta acá arriba para que Compras/Logística reaccionen. */}
      {faltantesCount > 0 && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="faltantes-alarm-banner">
          <AlertTriangle size={15} className="text-red-600 flex-shrink-0" />
          <p className="text-xs font-bold text-red-700">
            {faltantesCount} {faltantesCount === 1 ? "línea" : "líneas"} con faltante pendiente
            {faltantesRetiroCount > 0 && ` — ${faltantesRetiroCount} de retiro`}
            {faltantesEntregaCount > 0 && ` — ${faltantesEntregaCount} de entrega`}
          </p>
        </div>
      )}

      {/* Sub-tabs — BLOQUE 44: el Operario Electricista nunca ve "Retiro de
          Materiales". BLOQUE 55: se agrega "Itinerario" (visible para todos,
          incluido el Operario — solo lectura salvo Jefe de Compras/Admin). */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit flex-wrap">
        {!operarioMode && (
          <button
            onClick={() => setSubTab("retiro")}
            className={`relative flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${subTab === "retiro" ? "bg-white text-[#2E6DA4] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            data-testid="sub-tab-retiro"
          >
            <Truck size={12} /> Retiro de Materiales
            {faltantesRetiroCount > 0 && <span className="ml-1 bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold animate-pulse" data-testid="faltante-retiro-badge">{faltantesRetiroCount}</span>}
            {pendingWDs.length > 0 && <span className="ml-1 bg-amber-400 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">{pendingWDs.length}</span>}
          </button>
        )}
        <button
          onClick={() => setSubTab("entrega")}
          className={`relative flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${subTab === "entrega" ? "bg-white text-[#2E6DA4] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          data-testid="sub-tab-entrega"
        >
          <Package size={12} /> Entrega en Obra
          {faltantesEntregaCount > 0 && <span className="ml-1 bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold animate-pulse" data-testid="faltante-entrega-badge">{faltantesEntregaCount}</span>}
          {closedRetiroWDs.length > 0 && <span className="ml-1 bg-lime-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">{closedRetiroWDs.length}</span>}
        </button>
        <button
          onClick={() => setSubTab("itinerario")}
          className={`relative flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${subTab === "itinerario" ? "bg-white text-[#2E6DA4] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          data-testid="sub-tab-itinerario"
        >
          <Route size={12} /> Itinerario
        </button>
      </div>

      {/* ── Sub-tab 1: Retiro de Materiales (nunca visible para el Operario Electricista) ── */}
      {!operarioMode && subTab === "retiro" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{retiroSubtabWDs.length} de {withdrawals.length} registros · {pendingWDs.length} pendientes · {deliveredWDs.length} completados</p>
            {canLogistics && eligibleOCs.length > 0 && (
              <button onClick={() => setShowNewModal(true)} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-3 py-2 rounded-xl text-xs font-semibold transition-all" data-testid="new-withdrawal-btn">
                <Plus size={14} /> Nuevo Retiro
              </button>
            )}
          </div>
          {loading ? <Skeleton className="h-48" /> : retiroSubtabWDs.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200/60 p-12 text-center">
              <Truck size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No hay retiros registrados</p>
              <p className="text-xs text-gray-400 mt-1">Los retiros se generan desde OCs aprobadas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {retiroSubtabWDs.map(wd => (
                <div key={wd._id} className={`bg-white rounded-xl border p-4 shadow-sm ${esFaltantePendiente(wd) ? "border-red-300 ring-1 ring-red-100" : "border-gray-200/60"}`} data-testid="withdrawal-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-gray-900 text-sm font-mono">{wd.number}</p>
                        <StatusBadge status={wd.status} />
                        {wd.es_faltante && wd.faltante_tipo === "retiro" && esFaltantePendiente(wd) && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse" data-testid={`faltante-badge-${wd._id}`}>
                            RETIRO INCOMPLETO — Faltante
                          </span>
                        )}
                        {wd.delivery_number && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold border border-emerald-200 font-mono">{wd.delivery_number}</span>}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">OC: <span className="font-mono">{wd.oc_number}</span> · {wd.project_name}</p>
                      <p className="text-xs text-gray-400">{wd.provider_name} · {formatDate(wd.created_at)}</p>
                      {wd.origen_retiro_number && <p className={`text-[10px] mt-0.5 ${esFaltantePendiente(wd) ? "text-red-500" : "text-gray-400"}`}>Generado por faltante de <span className="font-mono font-semibold">{wd.origen_retiro_number}</span></p>}
                      {wd.faltante_hijo_number && <p className="text-[10px] text-amber-600 mt-0.5">Faltante derivado: <span className="font-mono font-semibold">{wd.faltante_hijo_number}</span></p>}
                      {wd.retiro_closed_by_name && <p className="text-[10px] text-gray-400 mt-0.5">Cerrado por: {wd.retiro_closed_by_name} · {formatDate(wd.retiro_closed_at)}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => setViewWD(wd)} className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all" data-testid={`view-wd-${wd._id}`}><Eye size={12} /> VER</button>
                      <button onClick={() => generateREPDF(wd)} className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-red-50 text-red-700 border border-red-100 rounded-lg hover:bg-red-100 transition-all" data-testid={`pdf-wd-${wd._id}`}><Download size={12} /> PDF</button>
                      {(() => {
                        const docs = getWdDocs(wd);
                        return docs.length > 0 ? (
                          <button onClick={() => setViewWD(wd)} className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg hover:bg-emerald-100 transition-all" data-testid={`doc-wd-${wd._id}`} title="Ver remisiones adjuntas">
                            <Paperclip size={12} /> {docs.length} {docs.length === 1 ? "Remisión" : "Remisiones"}
                          </button>
                        ) : canCloseRetiro ? (
                          <button onClick={() => handleUploadDoc(wd)} className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-dashed border-gray-300 text-gray-400 rounded-lg hover:bg-gray-50 hover:text-gray-600 transition-all" data-testid={`upload-wd-${wd._id}`} title="Adjuntar foto del remito del proveedor">
                            <UploadCloud size={12} /> Remito
                          </button>
                        ) : null;
                      })()}
                      {wd.status === "pendiente" && canEditIndicaciones && (
                        <button
                          onClick={() => { setIndicWD(wd); setIndicItems(wd.items.map(i => ({ ...i, indicaciones: i.indicaciones || "" }))); }}
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 font-semibold transition-all"
                          data-testid={`indicaciones-wd-${wd._id}`}
                          title="Agregar indicaciones por ítem para quien retire el material"
                        >
                          <MessageSquare size={12} /> Indicaciones
                        </button>
                      )}
                      {wd.status === "pendiente" && canCloseRetiro && (
                        <button
                          onClick={() => { setCloseRetiroWD(wd); setRetiroItems(wd.items.map(i => ({ ...i, retirado: true, cantidad_retirada: i.quantity, retiro_notes: i.retiro_notes || "" }))); setRetiroNotes(""); }}
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-lime-500 text-white rounded-lg hover:bg-lime-600 font-semibold transition-all"
                          data-testid={`close-retiro-${wd._id}`}
                        >
                          <Check size={12} /> Cerrar Retiro
                        </button>
                      )}
                      {canEditItinerario && (
                        <button
                          onClick={() => openAddItinerario(wd, "retiro")}
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-lg hover:bg-sky-100 font-semibold transition-all"
                          data-testid={`add-itinerario-retiro-${wd._id}`}
                          title="Agregar este retiro al itinerario de la semana"
                        >
                          <Route size={12} /> Itinerario
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex gap-2 flex-wrap">
                      {agruparRetiro(wd.items).slice(0, 4).map((item, i) => {   /* Fix FF */                        const retirada = item.cantidad_retirada != null ? item.cantidad_retirada : (item.retirado ? item.quantity : 0);
                        return (
                        <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full border ${item.retirado ? "bg-lime-50 text-lime-700 border-lime-200" : "bg-gray-50 text-gray-500 border-gray-200"}`}>
                          {item.retirado ? "✓" : "○"} {item.item_name} ({retirada}/{item.quantity})
                        </span>
                        );
                      })}
                      {agruparRetiro(wd.items).length > 4 && <span className="text-[10px] text-gray-400">+{agruparRetiro(wd.items).length - 4} más</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Sub-tab 2: Entrega en Obra ── */}
      {subTab === "entrega" && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">{closedRetiroWDs.length} en tránsito · {deliveredWDs.length} entregados</p>
          {loading ? <Skeleton className="h-48" /> : [...closedRetiroWDs, ...deliveredWDs].length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200/60 p-12 text-center">
              <Package size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No hay entregas pendientes</p>
              <p className="text-xs text-gray-400 mt-1">Las entregas aparecen cuando se cierra un retiro de materiales</p>
            </div>
          ) : (
            <div className="space-y-3">
              {[...closedRetiroWDs, ...deliveredWDs].map(wd => (
                <div key={wd._id} className={`bg-white rounded-xl border p-4 shadow-sm ${esFaltantePendiente(wd) ? "border-red-300 ring-1 ring-red-100" : "border-gray-200/60"}`} data-testid="delivery-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-gray-900 text-sm font-mono">{wd.number}</p>
                        <StatusBadge status={wd.status} />
                        {wd.es_faltante && wd.faltante_tipo === "entrega" && esFaltantePendiente(wd) && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-500 text-white animate-pulse" data-testid={`faltante-badge-${wd._id}`}>
                            ENTREGA INCOMPLETA — Faltante
                          </span>
                        )}
                        {wd.delivery_number && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold border border-emerald-200 font-mono">{wd.delivery_number}</span>}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">OC: <span className="font-mono">{wd.oc_number}</span> · {wd.project_name}</p>
                      <p className="text-xs text-gray-400">{wd.provider_name} · Retirado: {formatDate(wd.retiro_closed_at)}</p>
                      {wd.origen_retiro_number && <p className={`text-[10px] mt-0.5 ${esFaltantePendiente(wd) ? "text-amber-600" : "text-gray-400"}`}>Generado por faltante de <span className="font-mono font-semibold">{wd.origen_retiro_number}</span></p>}
                      {wd.faltante_hijo_number && <p className="text-[10px] text-amber-600 mt-0.5">Faltante derivado: <span className="font-mono font-semibold">{wd.faltante_hijo_number}</span></p>}
                      {wd.delivery_closed_by_name && <p className="text-[10px] text-gray-400 mt-0.5">Entregado por: {wd.delivery_closed_by_name} · {formatDate(wd.delivery_closed_at)}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => setViewWD(wd)} className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all" data-testid={`view-del-${wd._id}`}><Eye size={12} /> VER</button>
                      {wd.delivery_number && (
                        <button onClick={() => generateENPDF(wd)} className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg hover:bg-emerald-100 transition-all" data-testid={`pdf-en-${wd._id}`}><Download size={12} /> PDF Entrega</button>
                      )}
                      {wd.status === "retiro_cerrado" && canResidente && (
                        <button
                          onClick={() => { setCloseDeliveryWD(wd); setDeliveryItems(wd.items.map(i => { const base = i.cantidad_retirada != null ? i.cantidad_retirada : (i.retirado ? i.quantity : 0); return { ...i, entregado: true, cantidad_entregada: base, _cantidad_retirada_base: base, delivery_notes: i.delivery_notes || "" }; })); setDeliveryNotes(""); }}
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 font-semibold transition-all"
                          data-testid={`close-delivery-${wd._id}`}
                        >
                          <CheckCircle2 size={12} /> Confirmar Entrega
                        </button>
                      )}
                      {canEditItinerario && !operarioMode && (
                        <button
                          onClick={() => openAddItinerario(wd, "entrega")}
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-lg hover:bg-sky-100 font-semibold transition-all"
                          data-testid={`add-itinerario-entrega-${wd._id}`}
                          title="Agregar esta entrega al itinerario de la semana"
                        >
                          <Route size={12} /> Itinerario
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                    {agruparRetiro(wd.items).map((item, i) => {   /* Fix FF */
                      const retirada = item.cantidad_retirada != null ? item.cantidad_retirada : (item.retirado ? item.quantity : 0);
                      const entregada = item.cantidad_entregada != null ? item.cantidad_entregada : (item.entregado ? retirada : 0);
                      return (
                      <div key={i} className="flex items-center gap-3 text-xs">
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${item.entregado ? "bg-emerald-500 text-white" : item.retirado ? "bg-lime-400 text-white" : "bg-gray-200 text-gray-400"}`}>
                          {(item.entregado || item.retirado) ? "✓" : ""}
                        </span>
                        <span className="flex-1 text-gray-700">{item.item_name}</span>
                        <span className="text-gray-400">{entregada}/{retirada} {item.unit}</span>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Sub-tab 3: Itinerario (BLOQUE 55) — Jefe de Compras edita, el
          resto de los perfiles habilitados ve en solo lectura. ── */}
      {subTab === "itinerario" && (
        <ItinerarioTab
          userRole={userRole}
          onOpenStop={(stop) => {
            // BLOQUE 65 PARTE A — abre el detalle SOBRE el itinerario (sin
            // cambiar de sub-pestaña); el pedido del cliente fue justamente
            // no perder la posición del calendario al ver un pedido.
            // Fix AH (2026-09-04): llega la parada completa para que el detalle
            // muestre solo los ítems marcados para ella.
            const target = withdrawals.find(w => w._id === stop.withdrawal_id) || withdrawals.find(w => w.number === stop.withdrawal_number);
            if (target) { setViewStop(stop); setSoloParada(true); setViewWD(target); }
          }}
        />
      )}

      {/* ── Modal: Nuevo Retiro ── */}
      <Modal open={showNewModal} onClose={() => { setShowNewModal(false); setSelectedOC(null); setNewNotes(""); }} title="Nuevo Retiro de Materiales" wide>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Orden de Compra *</label>
            {/* Fix EI (2026-09-27): "que el buscador vaya filtrando el despliegue conforme se vaya escribiendo" (CEN). */}
            <SelectBuscable value={selectedOC?._id || ""} onChange={v => handleSelectOC(v)}
              options={eligibleOCs.map(oc => ({ value: oc._id, label: `${oc.number} — ${oc.provider_name} (${formatPYGCompact(oc.total)})${oc.lista_obra_nombre || oc.project_name ? ` · ${oc.lista_obra_nombre || oc.project_name}` : ""}` }))}
              vacio="Seleccionar OC aprobada..." placeholderBuscar="Buscar N° de OC, proveedor u obra..."
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white" testId="wd-oc-select" />
          </div>
          {selectedOC && (
            <>
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                <p className="text-xs font-semibold text-blue-800">{selectedOC.number}</p>
                <p className="text-xs text-blue-600">{selectedOC.provider_name} · {selectedOC.project_name}</p>
                {selectedOC.sucursal && <p className="text-xs text-blue-500 mt-0.5">Sucursal de retiro: {selectedOC.sucursal}</p>}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">Ítems de la OC</label>
                {canEditIndicaciones && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5 mb-2" data-testid="indic-create-hint">
                    Como Jefe de Compras podés agregar indicaciones por ítem (ej. "retirar en sucursal Luque", "pedir que lo embalen"). Quedan visibles para quien retire el material y en el PDF.
                  </p>
                )}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-3 py-2.5 text-gray-600 font-semibold">Descripción</th>
                        <th className="text-center px-2 py-2.5 text-gray-600 font-semibold">UM</th>
                        <th className="text-right px-2 py-2.5 text-gray-600 font-semibold">Cant.</th>
                        <th className="text-right px-2 py-2.5 text-gray-600 font-semibold">P.U.</th>
                        <th className="text-right px-2 py-2.5 text-gray-600 font-semibold">Subtotal</th>
                        {canEditIndicaciones && <th className="text-left px-3 py-2.5 text-amber-700 font-semibold">Indic.</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {agruparPorMaterial(selectedOC.items, true).map((item, i) => (   /* Fix FF: una fila por material */
                        <tr key={i} className="border-b border-gray-100 last:border-0" data-testid={`new-wd-row-${i}`}>
                          <td className="px-3 py-2.5 text-gray-700">{item.item_name}</td>
                          <td className="px-2 py-2.5 text-center text-gray-500">{item.unit || "un"}</td>
                          <td className="px-2 py-2.5 text-right text-gray-700">{item.quantity}</td>
                          <td className="px-2 py-2.5 text-right text-gray-600 font-mono">{formatPYGCompact(item.unit_price)}</td>
                          <td className="px-2 py-2.5 text-right text-[#2E6DA4] font-mono font-semibold">{formatPYGCompact(item.subtotal)}</td>
                          {canEditIndicaciones && (
                            <td className="px-3 py-2.5">
                              <input type="text" value={newIndic[item.__idx[0]] || ""} onChange={e => setNewIndic({ ...newIndic, ...Object.fromEntries(item.__idx.map(j => [j, e.target.value])) })}
                                placeholder="Indicación..." className="w-full border border-amber-200 bg-amber-50/40 rounded-lg px-2 py-1 text-xs"
                                data-testid={`new-indic-input-${i}`} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
            <textarea value={newNotes} onChange={e => setNewNotes(e.target.value)} rows={2} placeholder="Instrucciones de retiro, horarios acordados, etc." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => { setShowNewModal(false); setSelectedOC(null); setNewNotes(""); setNewIndic({}); }} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button onClick={handleCreateWithdrawal} disabled={saving || !selectedOC} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="create-withdrawal-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Truck size={15} />}{saving ? "Creando..." : "Crear Retiro"}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Modal: Cerrar Retiro ── */}
      {closeRetiroWD && (
        <Modal open={!!closeRetiroWD} onClose={() => setCloseRetiroWD(null)} title={`Cerrar Retiro · ${closeRetiroWD.number}`} wide>
          <div className="space-y-4">
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
              <p className="text-xs font-semibold text-amber-800">OC: {closeRetiroWD.oc_number} · {closeRetiroWD.provider_name}</p>
              <p className="text-xs text-amber-600 mt-0.5">Marque los ítems retirados; si retiró menos de lo pedido, ajuste la cantidad — se generará un nuevo retiro con lo faltante.</p>
              {closeRetiroWD.items?.some(i => i.indicaciones) && (
                <p className="text-[11px] text-amber-700 mt-1 font-medium" data-testid="close-retiro-indic-hint">
                  Este retiro tiene indicaciones del Jefe de Compras — revíselas en la columna "Indicaciones" antes de retirar.
                </p>
              )}
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-center px-3 py-2.5 text-gray-600 font-semibold w-12">Retirado</th>
                    <th className="text-left px-3 py-2.5 text-gray-600 font-semibold">Descripción</th>
                    <th className="text-center px-2 py-2.5 text-gray-600 font-semibold">UM</th>
                    <th className="text-right px-2 py-2.5 text-gray-600 font-semibold">Pedido</th>
                    <th className="text-right px-2 py-2.5 text-lime-700 font-semibold w-24">Cant. Retirada</th>
                    <th className="text-left px-3 py-2.5 text-amber-700 font-semibold">Indicaciones</th>
                    <th className="text-left px-3 py-2.5 text-gray-600 font-semibold">Obs.</th>
                  </tr>
                </thead>
                <tbody>
                  {agruparRetiro(retiroItems).map((item, i) => {   /* Fix FF: una fila por material */
                    const faltante = _r4(Math.max(0, Number(item.quantity || 0) - Number(item.cantidad_retirada || 0)));
                    return (
                    <tr key={i} className={`border-b border-gray-100 last:border-0 transition-colors ${faltante > 0.0001 ? "bg-amber-50/60" : item.retirado ? "bg-lime-50/50" : ""}`}>
                      <td className="px-3 py-2.5 text-center">
                        <input type="checkbox" checked={item.retirado} onChange={e => { const checked = e.target.checked; setRetiroItems(actualizarGrupo(retiroItems, item, null, null, l => ({ ...l, retirado: checked, cantidad_retirada: checked ? l.quantity : 0 }))); }} className="w-4 h-4 accent-lime-500 cursor-pointer" data-testid={`retiro-check-${i}`} />
                      </td>
                      <td className="px-3 py-2.5 text-gray-700">{item.item_name}</td>
                      <td className="px-2 py-2.5 text-center text-gray-500">{item.unit}</td>
                      <td className="px-2 py-2.5 text-right text-gray-700">{item.quantity}</td>
                      <td className="px-2 py-2.5 text-right">
                        <input type="number" min="0" step="any" value={item.cantidad_retirada} onChange={e => { const val = Math.max(0, Number(e.target.value)); setRetiroItems(actualizarGrupo(retiroItems, item, val, l => l.quantity, (l, v) => ({ ...l, cantidad_retirada: v, retirado: v >= Number(l.quantity || 0) - 0.0001 }))); }} className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-xs text-right" data-testid={`retiro-qty-${i}`} />
                        {faltante > 0.0001 && <p className="text-[10px] text-amber-700 font-semibold mt-0.5">Faltan {faltante}</p>}
                      </td>
                      <td className="px-3 py-2.5" data-testid={`close-retiro-indic-${i}`}>
                        {item.indicaciones
                          ? <span className="inline-block bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-2 py-1 leading-snug">{item.indicaciones}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <input type="text" value={item.retiro_notes} onChange={e => { const v = e.target.value; setRetiroItems(actualizarGrupo(retiroItems, item, null, null, l => ({ ...l, retiro_notes: v }))); }} placeholder="Observación..." className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs" />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones generales</label>
              <textarea value={retiroNotes} onChange={e => setRetiroNotes(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setCloseRetiroWD(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={() => handleCloseRetiro(false)} disabled={saving} className="flex-1 bg-lime-500 hover:bg-lime-600 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="confirm-close-retiro-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}{saving ? "Guardando..." : "Cerrar Retiro"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Indicaciones por ítem (Jefe de Compras) — BLOQUE 40 PARTE C ── */}
      {indicWD && (
        <Modal open={!!indicWD} onClose={() => setIndicWD(null)} title={`Indicaciones · ${indicWD.number}`} wide>
          <div className="space-y-4" data-testid="indicaciones-modal">
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
              <p className="text-xs font-semibold text-amber-800">OC: {indicWD.oc_number} · {indicWD.provider_name}</p>
              <p className="text-xs text-amber-600 mt-0.5">Indicaciones por ítem para quien retire el material (ej. "retirar en sucursal Luque", "pedir que lo embalen"). Se guardan sin cerrar el retiro y quedan visibles para el resto de los perfiles habilitados.</p>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2.5 text-gray-600 font-semibold">Descripción</th>
                    <th className="text-center px-2 py-2.5 text-gray-600 font-semibold">UM</th>
                    <th className="text-right px-2 py-2.5 text-gray-600 font-semibold">Cant.</th>
                    <th className="text-left px-3 py-2.5 text-amber-700 font-semibold">Indicaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {agruparRetiro(indicItems).map((item, i) => (   /* Fix FF: una fila por material */
                    <tr key={i} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-2.5 text-gray-700">{item.item_name}</td>
                      <td className="px-2 py-2.5 text-center text-gray-500">{item.unit}</td>
                      <td className="px-2 py-2.5 text-right text-gray-700">{item.quantity}</td>
                      <td className="px-3 py-2.5">
                        <input type="text" value={item.indicaciones}
                          onChange={e => { const v = e.target.value; setIndicItems(actualizarGrupo(indicItems, item, null, null, l => ({ ...l, indicaciones: v }))); }}
                          placeholder="Indicación para este ítem..."
                          className="w-full border border-amber-200 bg-amber-50/40 rounded-lg px-2 py-1 text-xs"
                          data-testid={`indic-input-${i}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setIndicWD(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium" data-testid="cancel-indic-btn">Cancelar</button>
              <button onClick={handleSaveIndicaciones} disabled={savingIndic} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="save-indicaciones-btn">
                {savingIndic ? <Loader2 size={15} className="animate-spin" /> : <MessageSquare size={15} />}{savingIndic ? "Guardando..." : "Guardar Indicaciones"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Confirmar Entrega en Obra ── */}
      {closeDeliveryWD && (
        <Modal open={!!closeDeliveryWD} onClose={() => setCloseDeliveryWD(null)} title={`Confirmar Entrega · ${closeDeliveryWD.number}`} wide>
          <div className="space-y-4">
            <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
              <p className="text-xs font-semibold text-emerald-800">RE: {closeDeliveryWD.number} · OC: {closeDeliveryWD.oc_number}</p>
              <p className="text-xs text-emerald-600 mt-0.5">Se confirma la recepción en obra; si entrega menos de lo retirado, ajuste la cantidad — se generará una nueva entrega con lo faltante.</p>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-center px-3 py-2.5 text-gray-600 font-semibold w-12">Entregado</th>
                    <th className="text-left px-3 py-2.5 text-gray-600 font-semibold">Descripción</th>
                    <th className="text-center px-2 py-2.5 text-gray-600 font-semibold">UM</th>
                    <th className="text-right px-2 py-2.5 text-gray-600 font-semibold">Retirado</th>
                    <th className="text-right px-2 py-2.5 text-emerald-700 font-semibold w-24">Cant. Entregada</th>
                    <th className="text-left px-3 py-2.5 text-gray-600 font-semibold">Obs.</th>
                  </tr>
                </thead>
                <tbody>
                  {agruparRetiro(deliveryItems).map((item, i) => {   /* Fix FF: una fila por material */
                    const base = item._cantidad_retirada_base != null ? item._cantidad_retirada_base : item.quantity;
                    const faltante = _r4(Math.max(0, Number(base || 0) - Number(item.cantidad_entregada || 0)));
                    return (
                    <tr key={i} className={`border-b border-gray-100 last:border-0 transition-colors ${faltante > 0.0001 ? "bg-red-50/60" : item.entregado ? "bg-emerald-50/50" : ""}`}>
                      <td className="px-3 py-2.5 text-center">
                        <input type="checkbox" checked={item.entregado} onChange={e => { const checked = e.target.checked; setDeliveryItems(actualizarGrupo(deliveryItems, item, null, null, l => ({ ...l, entregado: checked, cantidad_entregada: checked ? l._cantidad_retirada_base : 0 }))); }} className="w-4 h-4 accent-emerald-500 cursor-pointer" data-testid={`delivery-check-${i}`} />
                      </td>
                      <td className="px-3 py-2.5 text-gray-700">{item.item_name}</td>
                      <td className="px-2 py-2.5 text-center text-gray-500">{item.unit}</td>
                      <td className="px-2 py-2.5 text-right text-gray-700">{base}</td>
                      <td className="px-2 py-2.5 text-right">
                        <input type="number" min="0" max={base} step="any" value={item.cantidad_entregada} onChange={e => { const val = Math.max(0, Math.min(Number(e.target.value), base)); setDeliveryItems(actualizarGrupo(deliveryItems, item, val, l => l._cantidad_retirada_base, (l, v) => ({ ...l, cantidad_entregada: v, entregado: v >= Number(l._cantidad_retirada_base || 0) - 0.0001 }))); }} className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-xs text-right" data-testid={`delivery-qty-${i}`} />
                        {faltante > 0.0001 && <p className="text-[10px] text-red-700 font-semibold mt-0.5">Faltan {faltante}</p>}
                      </td>
                      <td className="px-3 py-2.5">
                        <input type="text" value={item.delivery_notes} onChange={e => { const v = e.target.value; setDeliveryItems(actualizarGrupo(deliveryItems, item, null, null, l => ({ ...l, delivery_notes: v }))); }} placeholder="Observación..." className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs" />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones de entrega</label>
              <textarea value={deliveryNotes} onChange={e => setDeliveryNotes(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setCloseDeliveryWD(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={() => handleCloseDelivery(false)} disabled={saving} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="confirm-delivery-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}{saving ? "Guardando..." : "Confirmar Entrega"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Ver Detalle (RE / EN) ── */}
      {viewWD && (
        <Modal open={!!viewWD} onClose={() => setViewWD(null)} title={`Detalle · ${viewWD.number}`} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">N° Remisión</p><p className="font-bold text-gray-900 font-mono mt-0.5">{viewWD.number}</p></div>
              <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">Estado</p><div className="mt-0.5"><StatusBadge status={viewWD.status} /></div></div>
              <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">OC Referencia</p><p className="text-sm text-gray-800 font-mono mt-0.5">{viewWD.oc_number}</p></div>
              <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">Proveedor</p><p className="text-sm text-gray-800 mt-0.5">{viewWD.provider_name}</p></div>
              <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">Proyecto</p><p className="text-sm text-gray-800 mt-0.5">{viewWD.project_name}</p></div>
              {viewWD.delivery_number ? (
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100"><p className="text-[10px] text-emerald-500 uppercase font-semibold tracking-wide">N° Entrega (EN)</p><p className="text-sm text-emerald-800 font-mono font-bold mt-0.5">{viewWD.delivery_number}</p></div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">Sucursal Retiro</p><p className="text-sm text-gray-800 mt-0.5">{viewWD.sucursal || "—"}</p></div>
              )}
            </div>
            {/* Fix AH (2026-09-04): abierto desde el itinerario — qué parada es y
                cuántos de los ítems del retiro le corresponden. */}
            {stopActivo && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="view-wd-parada-banner">
                <p className="text-xs text-sky-800">
                  <span className="font-bold">Parada del itinerario</span>
                  {" · "}{parseDateOnly(stopActivo.fecha).toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" })}
                  {stopActivo.sucursal ? ` · ${stopActivo.sucursal}` : ""}
                  {" · "}{stopActivo.items_idx?.length ? `${agruparRetiro(viewWD.items).filter(g => g.__idx.some(j => stopActivo.items_idx.includes(j))).length} de ${agruparRetiro(viewWD.items).length} ítems` : `todos los ítems (${agruparRetiro(viewWD.items).length})`}
                </p>
                {stopActivo.items_idx?.length > 0 && (
                  <button type="button" onClick={() => setSoloParada(v => !v)} className="text-[11px] font-semibold text-sky-700 hover:underline" data-testid="view-wd-parada-toggle">
                    {soloParada ? "Ver todos los ítems del retiro" : "Ver solo los de esta parada"}
                  </button>
                )}
              </div>
            )}
            {/* Fix O (2026-08-31): historial del pedido de origen en cada paso del flujo. */}
            <HistorialPedidoCard pmId={viewWD.pm_origen_id} />
            {viewWD.es_faltante && (
              <div className={`rounded-xl p-3 border ${esFaltantePendiente(viewWD) ? (viewWD.faltante_tipo === "retiro" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200") : "bg-gray-50 border-gray-200"}`} data-testid="view-wd-faltante-banner">
                {esFaltantePendiente(viewWD) && (
                  <p className={`text-xs font-bold ${viewWD.faltante_tipo === "retiro" ? "text-red-700" : "text-amber-700"}`}>
                    {viewWD.faltante_tipo === "retiro" ? "RETIRO INCOMPLETO — Faltante" : "ENTREGA INCOMPLETA — Faltante"}
                  </p>
                )}
                <p className="text-[11px] text-gray-600 mt-0.5">Generado automáticamente por el cierre incompleto de <span className="font-mono font-semibold">{viewWD.origen_retiro_number}</span></p>
              </div>
            )}
            {viewWD.faltante_hijo_number && (
              <div className="rounded-xl p-3 border bg-amber-50 border-amber-200" data-testid="view-wd-faltante-hijo-banner">
                <p className="text-xs font-bold text-amber-700">Tiene un faltante pendiente</p>
                <p className="text-[11px] text-gray-600 mt-0.5">Se generó <span className="font-mono font-semibold">{viewWD.faltante_hijo_number}</span> con los ítems/cantidades que quedaron pendientes.</p>
              </div>
            )}
            {(viewWD.retiro_closed_by_name || viewWD.delivery_closed_by_name || viewWD.indicaciones_updated_by_name) && (
              <div className="grid grid-cols-2 gap-3" data-testid="wd-registro-operaciones">
                {viewWD.retiro_closed_by_name && (
                  <div className="bg-lime-50 rounded-xl p-3 border border-lime-100" data-testid="wd-retiro-registro">
                    <p className="text-[10px] text-lime-600 uppercase font-semibold tracking-wide">Retiro cerrado por</p>
                    <p className="text-sm text-lime-900 font-semibold mt-0.5">{viewWD.retiro_closed_by_name}</p>
                    <p className="text-[10px] text-lime-600">{formatDateTime(viewWD.retiro_closed_at)}</p>
                  </div>
                )}
                {viewWD.delivery_closed_by_name && (
                  <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100" data-testid="wd-entrega-registro">
                    <p className="text-[10px] text-emerald-600 uppercase font-semibold tracking-wide">Entrega confirmada por</p>
                    <p className="text-sm text-emerald-900 font-semibold mt-0.5">{viewWD.delivery_closed_by_name}</p>
                    <p className="text-[10px] text-emerald-600">{formatDateTime(viewWD.delivery_closed_at)}</p>
                  </div>
                )}
                {viewWD.indicaciones_updated_by_name && (
                  <div className="bg-amber-50 rounded-xl p-3 border border-amber-100" data-testid="wd-indic-registro">
                    <p className="text-[10px] text-amber-600 uppercase font-semibold tracking-wide">Indicaciones cargadas por</p>
                    <p className="text-sm text-amber-900 font-semibold mt-0.5">{viewWD.indicaciones_updated_by_name}</p>
                    <p className="text-[10px] text-amber-600">{formatDateTime(viewWD.indicaciones_updated_at)}</p>
                  </div>
                )}
              </div>
            )}
            <div className="border border-gray-200 rounded-xl p-3" data-testid="wd-remisiones-section">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide">Remisiones Adjuntas ({getWdDocs(viewWD).length}/{MAX_WD_REMISIONES})</p>
                {canCloseRetiro && getWdDocs(viewWD).length < MAX_WD_REMISIONES && (
                  <button onClick={() => handleUploadDoc(viewWD)} className="flex items-center gap-1 text-[11px] px-2 py-1 border border-dashed border-gray-300 text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700 transition-all" data-testid="add-remision-btn">
                    <UploadCloud size={11} /> Agregar remisión
                  </button>
                )}
              </div>
              {getWdDocs(viewWD).length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">Sin remisiones adjuntas</p>
              ) : (
                <div className="space-y-1.5">
                  {getWdDocs(viewWD).map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-2.5 py-1.5" data-testid={`remision-item-${idx}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip size={12} className="text-emerald-600 flex-shrink-0" />
                        <span className="text-xs text-gray-700 truncate" title={doc.doc_name}>{doc.doc_name}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Fix AE (2026-09-03): el listado ya no trae el archivo; se abre bajo demanda */}
                        <a href={`${API}/purchases/material-withdrawals/${viewWD._id}/documents/${idx}/file?inline=1`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-800 p-1.5 hover:bg-emerald-50 rounded-lg transition-all" data-testid={`remision-view-${idx}`} title="Ver / descargar">
                          <Eye size={13} />
                        </a>
                        {canCloseRetiro && (
                          <button onClick={() => handleDeleteDoc(viewWD._id, idx)} className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded-lg transition-all" data-testid={`remision-delete-${idx}`} title="Eliminar remisión">
                            <X size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2.5 text-gray-600 font-semibold">Ítem</th>
                    <th className="text-center px-2 py-2.5 text-gray-600 font-semibold">UM</th>
                    <th className="text-right px-2 py-2.5 text-gray-600 font-semibold">Cant.</th>
                    <th className="text-left px-3 py-2.5 text-amber-700 font-semibold">Indicaciones</th>
                    <th className="text-center px-2 py-2.5 text-lime-700 font-semibold">Retirado</th>
                    <th className="text-center px-2 py-2.5 text-emerald-700 font-semibold">Entregado</th>
                    <th className="text-center px-2 py-2.5 text-amber-700 font-semibold">Sobrante</th>
                  </tr>
                </thead>
                <tbody>
                  {agruparRetiro(viewWD.items).map((item, i) => {   /* Fix FF: una fila por material */
                    // Fix AH (2026-09-04): desde el itinerario, solo las filas de la parada
                    // (mismo índice que usa items_idx). Se conserva `i` para testids e indicaciones.
                    if (soloParada && stopActivo?.items_idx?.length && !item.__idx.some(j => stopActivo.items_idx.includes(j))) return null;
                    const retirada = item.cantidad_retirada != null ? item.cantidad_retirada : (item.retirado ? item.quantity : 0);
                    const entregada = item.cantidad_entregada != null ? item.cantidad_entregada : (item.entregado ? retirada : 0);
                    const faltaRetirar = Math.max(0, Number(item.quantity || 0) - retirada);
                    return (
                    <tr key={i} className={`border-b border-gray-100 last:border-0 ${faltaRetirar > 0.0001 ? "bg-red-50/40" : ""}`}>
                      <td className="px-3 py-2.5 text-gray-700">{item.item_name}</td>
                      <td className="px-2 py-2.5 text-center text-gray-500">{item.unit}</td>
                      <td className="px-2 py-2.5 text-right text-gray-700">{item.quantity}</td>
                      <td className="px-3 py-2.5" data-testid={`view-indic-${i}`}>
                        {item.indicaciones
                          ? <span className="inline-block bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-2 py-1 leading-snug">{item.indicaciones}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        <span className={`font-semibold ${item.retirado ? "text-lime-700" : faltaRetirar > 0.0001 ? "text-red-600" : "text-gray-400"}`}>{retirada}/{item.quantity}</span>
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        <span className={`font-semibold ${item.entregado ? "text-emerald-700" : "text-gray-400"}`}>{entregada}/{retirada}</span>
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        {/* Fix O (2026-08-31): el aviso "Retirar sobrante de material" aparecía en
                            la campanita y NO SE PODÍA APAGAR NUNCA, porque el endpoint que lo cierra
                            no tenía botón. Es por ítem: solo aparece si ese ítem tiene fecha límite
                            de uso y todavía no fue marcado como retirado. */}
                        {item.fecha_limite_uso && !item.sobrante_retirado ? (
                          <button
                            type="button"
                            onClick={() => handleMarcarSobrante(viewWD._id, item.__idx.filter(j => viewWD.items[j].fecha_limite_uso && !viewWD.items[j].sobrante_retirado))}
                            className="text-[10px] bg-amber-100 hover:bg-amber-200 text-amber-800 px-2 py-1 rounded-lg font-semibold border border-amber-200"
                            data-testid={`marcar-sobrante-btn-${i}`}
                          >
                            Marcar sobrante retirado
                          </button>
                        ) : item.fecha_limite_uso ? (
                          <span className="text-[10px] text-emerald-600 font-semibold">Retirado</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {viewWD.history && viewWD.history.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Historial</p>
                {viewWD.history.map((h, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full bg-[#2E6DA4] mt-1.5 flex-shrink-0" />
                    <div><span className="font-semibold text-gray-700">{h.action}</span><span className="text-gray-400 ml-2">{formatDate(h.at)}</span>{h.by && <span className="text-gray-400 ml-2">· {h.by}</span>}{h.comment && <p className="text-gray-500 mt-0.5">{h.comment}</p>}</div>
                  </div>
                ))}
              </div>
            )}
            {/* BLOQUE 65 PARTE A — acciones del rol disponibles SIN salir del
                detalle (así el itinerario nunca obliga a volver manualmente):
                mismas condiciones que las cards de Retiro/Entrega. */}
            <div className="flex gap-2 pt-2 border-t border-gray-100 flex-wrap" data-testid="view-wd-actions">
              <button
                onClick={() => { setSubTab(viewWD.status === "pendiente" ? "retiro" : "entrega"); setViewWD(null); }}
                className="flex items-center gap-1.5 text-xs px-3 py-2 border border-[#2E6DA4]/30 text-[#2E6DA4] rounded-xl hover:bg-[#EBF2F9] font-semibold transition-all"
                data-testid="open-in-retiro-entrega-btn"
              >
                <ExternalLink size={13} /> Abrir en Retiro/Entrega
              </button>
              {viewWD.status === "pendiente" && canEditIndicaciones && (
                <button
                  onClick={() => { setIndicWD(viewWD); setIndicItems(viewWD.items.map(i => ({ ...i, indicaciones: i.indicaciones || "" }))); setViewWD(null); }}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-100 font-semibold transition-all"
                  data-testid="view-wd-indicaciones-btn"
                >
                  <MessageSquare size={13} /> Indicaciones
                </button>
              )}
              {viewWD.status === "pendiente" && canCloseRetiro && (
                <button
                  onClick={() => { setCloseRetiroWD(viewWD); setRetiroItems(viewWD.items.map(i => ({ ...i, retirado: true, cantidad_retirada: i.quantity, retiro_notes: i.retiro_notes || "" }))); setRetiroNotes(""); setViewWD(null); }}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-lime-500 text-white rounded-xl hover:bg-lime-600 font-semibold transition-all"
                  data-testid="view-wd-close-retiro-btn"
                >
                  <Check size={13} /> Cerrar Retiro
                </button>
              )}
              {viewWD.status === "retiro_cerrado" && canResidente && (
                <button
                  onClick={() => { setCloseDeliveryWD(viewWD); setDeliveryItems(viewWD.items.map(i => { const base = i.cantidad_retirada != null ? i.cantidad_retirada : (i.retirado ? i.quantity : 0); return { ...i, entregado: true, cantidad_entregada: base, _cantidad_retirada_base: base, delivery_notes: i.delivery_notes || "" }; })); setDeliveryNotes(""); setViewWD(null); }}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 font-semibold transition-all"
                  data-testid="view-wd-close-delivery-btn"
                >
                  <CheckCircle2 size={13} /> Confirmar Entrega
                </button>
              )}
            </div>
            <div className="flex gap-3 pt-2 border-t border-gray-100">
              {!operarioMode && (
                <button onClick={() => generateREPDF(viewWD)} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-xs font-semibold transition-all" data-testid="pdf-re-modal-btn"><Download size={14} /> PDF Remisión</button>
              )}
              {viewWD.delivery_number && (
                <button onClick={() => generateENPDF(viewWD)} className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-xs font-semibold transition-all" data-testid="pdf-en-modal-btn"><Download size={14} /> PDF Entrega</button>
              )}
              <button onClick={() => setViewWD(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">Cerrar</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Agregar al Itinerario (BLOQUE 55, exclusivo Jefe de Compras) ── */}
      {addItinWD && (
        <Modal open={!!addItinWD} onClose={() => setAddItinWD(null)} title={`Agregar al Itinerario · ${addItinWD.wd.number}`}>
          <div className="space-y-4" data-testid="add-itinerario-modal">
            <div className="bg-sky-50 rounded-xl p-3 border border-sky-100">
              <p className="text-xs font-semibold text-sky-800">{addItinWD.tipo === "retiro" ? addItinWD.wd.provider_name : addItinWD.wd.project_name}</p>
              <p className="text-xs text-sky-600 mt-0.5">OC: {addItinWD.wd.oc_number} · {addItinWD.tipo === "retiro" ? "Retiro" : "Entrega"}</p>
              <p className="text-[11px] text-sky-500 mt-1">Un mismo retiro puede tener varias paradas (una por sucursal) — repetí este paso para cada sucursal.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha de la parada *</label>
              <input type="date" value={itinFecha} onChange={e => setItinFecha(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="add-itinerario-fecha-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sucursal</label>
              <input type="text" value={itinSucursal} onChange={e => setItinSucursal(e.target.value)} placeholder="Ej. Sucursal Villa Elisa" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="add-itinerario-sucursal-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Ítems de esta parada (opcional — vacío = todos)</label>
              <div className="border border-gray-200 rounded-xl max-h-40 overflow-y-auto divide-y divide-gray-100">
                {agruparRetiro(addItinWD.wd.items).map((item, i) => (   /* Fix FF: una fila por material */
                  <label key={i} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={item.__idx.every(j => itinItemsSel[j])} onChange={e => setItinItemsSel({ ...itinItemsSel, ...Object.fromEntries(item.__idx.map(j => [j, e.target.checked])) })} className="w-3.5 h-3.5 accent-sky-500" data-testid={`add-itinerario-item-${i}`} />
                    <span className="text-gray-700">{item.item_name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
              <textarea value={itinNotes} onChange={e => setItinNotes(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="add-itinerario-notes-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setAddItinWD(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={handleAddToItinerario} disabled={savingItin || !itinFecha} className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="confirm-add-itinerario-btn">
                {savingItin ? <Loader2 size={15} className="animate-spin" /> : <Route size={15} />}{savingItin ? "Agregando..." : "Agregar Parada"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

